import { AnimatePresence, motion } from 'framer-motion'
import { NextSeo } from 'next-seo'
import Image from 'next/image'
import React, { useEffect, useState } from 'react'
import { useIsLoggedIn } from 'src/common/hooks/useIsLoggedIn'
import useRouterStatus from 'src/common/hooks/useRouterStatus'
import { Header } from '../Header'
import { NavigationPanel } from '../NavigationPanel'
import { anonNavlinks } from '../NavigationPanel/anonNavLinks'
import { defaultNavlinks } from '../NavigationPanel/defaultNavLinks'

type AppLayoutProps = {
    isLandingPage?: boolean
    pageTitle: string
    children?: React.ReactNode
    isSessionLoading?: boolean
    initialLoggedInStatus?: boolean
    metaDescription?: string
    metaKeywords?: string
    metaImage?: string
    metaTitle?: string
    isNoIndex?: boolean
}

export const AppLayout = ({
    isLandingPage = false,
    children,
    pageTitle = 'Yes.Fit',
    isSessionLoading,
    initialLoggedInStatus = false,
    metaDescription,
    metaImage,
    metaTitle = null,
    isNoIndex = false
}: AppLayoutProps) => {
    const isLoggedInRedux = useIsLoggedIn()
    const [isLoggedIn, setIsLoggedIn] = useState(initialLoggedInStatus)
    const { isLoading } = useRouterStatus()
    const [isNavigationPanelOpen, setIsNavigationPanelOpen] = useState(false)

    useEffect(() => {
        setIsLoggedIn(isLoggedInRedux)
    }, [isLoggedInRedux])

    const toggleNavPanel = () => {
        setIsNavigationPanelOpen(!isNavigationPanelOpen)
    }

    return (
        <div>
            <NextSeo
                noindex={isNoIndex}
                title={metaTitle ?? pageTitle}
                description={metaDescription}
                openGraph={{
                    title: metaTitle ?? pageTitle,
                    description:
                        metaDescription ??
                        'The Global Leader in Virtual Races and Fitness Challenges. Interactive, motivating, and fun. Earn medals and other rewards. Get started for free.',
                    images: [
                        {
                            url:
                                metaImage ??
                                'https://res.cloudinary.com/devion-villegas/image/upload/v1629400516/web-next_Home__tn2tpt.png'
                        }
                    ]
                }}
            />
            {isLandingPage ? null : (
                <div>
                    {!isLoggedIn ? (
                        <div className="relative bg-yf-purple">
                            <div className="max-w-7xl mx-auto py-3 px-3 sm:px-6 lg:px-8">
                                <div className="text-center sm:px-16">
                                    <p className="font-medium text-white">
                                        Join more than 400,000 virtual racers!
                                    </p>
                                </div>
                            </div>
                        </div>
                    ) : (
                        ' '
                    )}
                    <Header
                        title={pageTitle}
                        isNavigationPanelOpen={isNavigationPanelOpen}
                        openNavPanel={toggleNavPanel}
                        isLoggedIn={isLoggedIn}
                    />
                </div>
            )}
            <div>
                <div>
                    <NavigationPanel
                        closeNavPanel={() => {
                            if (!isLoggedIn) {
                                setIsNavigationPanelOpen(false)
                            }
                            if (isLoggedIn) {
                                setIsNavigationPanelOpen(false)
                            }
                        }}
                        isSessionLoading={isSessionLoading}
                        navlinks={isLoggedIn ? defaultNavlinks : anonNavlinks}
                        isLoggedIn={isLoggedIn}
                        isNavigationPanelOpen={isNavigationPanelOpen}
                        openNavPanel={toggleNavPanel}
                    />
                </div>

                <div className="mx-auto">
                    {isNavigationPanelOpen ? (
                        <motion.div
                            className="top-0 left-0 right-0 bottom-0 h-screen bg-black bg-opacity-60 z-40 fixed cursor-pointer"
                            initial={{ opacity: 1 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ ease: 'linear', duration: 0.3 }}
                            onClick={() => toggleNavPanel()}
                        />
                    ) : null}
                    <AnimatePresence>
                        {isLoading ? (
                            <div className="flex items-center justify-center h-screen relative">
                                <div className="animate-spin rounded-full h-40 w-40 border-b-4 border-yf-green absolute"></div>
                                <Image
                                    src="/yf-logo-text.png"
                                    width={128}
                                    height={128}
                                    className="absolute"
                                />
                            </div>
                        ) : (
                            <motion.div>{children}</motion.div>
                        )}
                    </AnimatePresence>
                </div>

                {isLandingPage ? null : (
                    <section className="bg-gradient-to-b from-[#EFF5F4] to-[#EDF5F3]">
                        <div className="px-4 pb-[5rem] pt-10 md:pb-10 mx-auto sm:px-6 lg:px-8 max-w-7xl">
                            <div className="px-4 text-center">
                                <h2 className="text-transparent bg-clip-text bg-gradient-to-r from-green-400 to-purple-500 font-bold text-4xl md:text-6xl">
                                    Get fit the fun way.
                                </h2>
                                <h4 className="text-gray-800 text-lg pt-2">
                                    Join the community that supports YOUR fitness, YOUR
                                    way.
                                </h4>
                            </div>
                            <hr className="mt-6 mb-6 border-gray-200" />
                            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-12 gap-y-12 gap-x-8 xl:gap-x-12">
                                <div className="col-span-2 md:col-span-4 xl:pr-8">
                                    <img
                                        className="w-12 h-12 flex-grow-0 flex-shrink-0 cursor-pointer"
                                        width="48px"
                                        height="48px"
                                        src="/logo.svg"
                                        alt="Yes.Fit logo"
                                    />

                                    <p className="text-base leading-relaxed text-gray-600 mt-3 md:mt-7">
                                        Our mission at Yes.Fit is to help you successfully
                                        create and maintain a healthier lifestyle by
                                        providing life-changing motivation.
                                    </p>

                                    <a
                                        href="/register"
                                        title=""
                                        className="inline-flex items-center justify-center px-10 py-4 font-medium text-white transition-all duration-200 bg-yf-green rounded-md hover:bg-blue-700 focus:bg-blue-700 mt-3 md:mt-7"
                                    >
                                        Join for Free
                                    </a>
                                </div>

                                <div className="lg:col-span-2">
                                    <p className="text-base font-semibold text-yf-purple">
                                        Fitness
                                    </p>

                                    <ul className="mt-6 space-y-5">
                                        <li>
                                            <a
                                                href="/races"
                                                title=""
                                                className="flex text-sm text-gray-800 transition-all duration-200 hover:text-yf-green focus:text-yf-green"
                                            >
                                                {' '}
                                                Races{' '}
                                            </a>
                                        </li>

                                        <li>
                                            <a
                                                href="/exercises"
                                                title=""
                                                className="flex text-sm text-gray-800 transition-all duration-200 hover:text-yf-green focus:text-yf-green"
                                            >
                                                {' '}
                                                Exercises{' '}
                                            </a>
                                        </li>

                                        <li>
                                            <a
                                                href="/challenges"
                                                title=""
                                                className="flex text-sm text-gray-800 transition-all duration-200 hover:text-yf-green focus:text-yf-green"
                                            >
                                                {' '}
                                                Challenges{' '}
                                            </a>
                                        </li>
                                        <li>
                                            <a
                                                href="/trainers"
                                                title=""
                                                className="flex text-sm text-gray-800 transition-all duration-200 hover:text-yf-green focus:text-yf-green"
                                            >
                                                {' '}
                                                Trainers{' '}
                                            </a>
                                        </li>

                                        <li>
                                            <a
                                                href="/store"
                                                title=""
                                                className="flex text-sm text-gray-800 transition-all duration-200 hover:text-yf-green focus:text-yf-green"
                                            >
                                                {' '}
                                                Store{' '}
                                            </a>
                                        </li>
                                    </ul>
                                </div>

                                <div className="lg:col-span-2">
                                    <p className="text-base font-semibold text-yf-purple">
                                        Yes.Fit Links
                                    </p>

                                    <ul className="mt-6 space-y-4">
                                        <li>
                                            <a
                                                href="/gift"
                                                title=""
                                                className="flex text-sm text-gray-800 transition-all duration-200 hover:text-yf-green focus:text-yf-green"
                                            >
                                                {' '}
                                                Gift Cards{' '}
                                            </a>
                                        </li>

                                        <li>
                                            <a
                                                href="https://help.yes.fit/en/"
                                                title=""
                                                className="flex text-sm text-gray-800 transition-all duration-200 hover:text-yf-green focus:text-yf-green"
                                            >
                                                {' '}
                                                FAQ{' '}
                                            </a>
                                        </li>

                                        <li>
                                            <a
                                                href="/community"
                                                title=""
                                                className="flex text-sm text-gray-800 transition-all duration-200 hover:text-yf-green focus:text-yf-green"
                                            >
                                                {' '}
                                                Community{' '}
                                            </a>
                                        </li>

                                        <li>
                                            <a
                                                href="/corporate-wellness"
                                                title=""
                                                className="flex text-sm text-gray-800 transition-all duration-200 hover:text-yf-green focus:text-yf-green"
                                            >
                                                {' '}
                                                Corp Wellness{' '}
                                            </a>
                                        </li>
                                        <li>
                                            <a
                                                href="/membership"
                                                title=""
                                                className="flex text-sm text-gray-800 transition-all duration-200 hover:text-yf-green focus:text-yf-green"
                                            >
                                                {' '}
                                                Membership{' '}
                                            </a>
                                        </li>

                                        <li>
                                            <a
                                                href="/blog"
                                                title=""
                                                className="flex text-sm text-gray-800 transition-all duration-200 hover:text-yf-green focus:text-yf-green"
                                            >
                                                {' '}
                                                Blog{' '}
                                            </a>
                                        </li>
                                    </ul>
                                </div>

                                <div className="lg:col-span-2">
                                    <p className="text-base font-semibold text-yf-purple">
                                        Get Our App
                                    </p>

                                    <div className="flex flex-col md:justify-center mt-3">
                                        <div className="mx-0 md:mx-1 mr-3 md:mr-0 my-3">
                                            <a
                                                href="https://yesfit.app.link/app-store"
                                                target="_blank"
                                                title="view our Yes.Fit app on the App Store"
                                                className="logo-ada-focus"
                                                role="button"
                                                rel="noreferrer"
                                            >
                                                <img
                                                    className="max-h-[50px] md:max-h-[30px]"
                                                    alt="Apple App Store Logo"
                                                    title="Apple App Store logo"
                                                    src="https://asset.yes.fit/homepage/App-Store-Badge-compressed.png"
                                                />
                                            </a>
                                        </div>
                                        <div className="mx-0 md:mx-1">
                                            <a
                                                href="https://yesfit.app.link/google-play"
                                                target="_blank"
                                                title="view our Yes.Fit app on the Google Play Store"
                                                className="logo-ada-focus"
                                                role="button"
                                                rel="noreferrer"
                                            >
                                                <img
                                                    className="max-h-[50px] md:max-h-[30px]"
                                                    alt="Google Play Store Logo"
                                                    title="Google Play Store logo"
                                                    src="https://asset.yes.fit/homepage/Google-Store-Badge-compressed.png"
                                                />
                                            </a>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <hr className="mt-16 mb-10 border-gray-200" />

                            <div className="sm:flex sm:items-center sm:justify-between mb-10 md:mb-0">
                                <p className="text-sm text-gray-600">
                                    © Copyright 2021, All Rights Reserved by Yes.Fit
                                </p>

                                <ul className="flex items-center mt-5 space-x-3 md:order-3 sm:mt-0">
                                    <li>
                                        <a
                                            href="https://races.yes.fit/privacy-policy"
                                            title=""
                                            className="flex text-sm text-gray-800 transition-all duration-200 hover:text-yf-green focus:text-yf-green"
                                        >
                                            {' '}
                                            Privacy Policy{' '}
                                        </a>
                                    </li>

                                    <li>
                                        <a
                                            href="/terms"
                                            title=""
                                            className="flex text-sm text-gray-800 transition-all duration-200 hover:text-yf-green focus:text-yf-green mr-4"
                                        >
                                            {' '}
                                            Terms and Conditions{' '}
                                        </a>
                                    </li>
                                    <li>
                                        <a
                                            href="https://twitter.com/Yesfit1"
                                            title="Yes.Fit on Twitter"
                                            className="flex items-center justify-center text-gray-800 transition-all duration-200 bg-transparent border border-gray-300 rounded-full w-7 h-7 focus:bg-yf-green hover:text-white focus:text-white hover:bg-yf-green hover:border-yf-green focus:border-yf-green"
                                        >
                                            <svg
                                                className="w-4 h-4"
                                                xmlns="http://www.w3.org/2000/svg"
                                                viewBox="0 0 24 24"
                                                fill="currentColor"
                                            >
                                                <path d="M19.633 7.997c.013.175.013.349.013.523 0 5.325-4.053 11.461-11.46 11.461-2.282 0-4.402-.661-6.186-1.809.324.037.636.05.973.05a8.07 8.07 0 0 0 5.001-1.721 4.036 4.036 0 0 1-3.767-2.793c.249.037.499.062.761.062.361 0 .724-.05 1.061-.137a4.027 4.027 0 0 1-3.23-3.953v-.05c.537.299 1.16.486 1.82.511a4.022 4.022 0 0 1-1.796-3.354c0-.748.199-1.434.548-2.032a11.457 11.457 0 0 0 8.306 4.215c-.062-.3-.1-.611-.1-.923a4.026 4.026 0 0 1 4.028-4.028c1.16 0 2.207.486 2.943 1.272a7.957 7.957 0 0 0 2.556-.973 4.02 4.02 0 0 1-1.771 2.22 8.073 8.073 0 0 0 2.319-.624 8.645 8.645 0 0 1-2.019 2.083z"></path>
                                            </svg>
                                        </a>
                                    </li>

                                    <li>
                                        <a
                                            href="https://www.facebook.com/YesFit/"
                                            title="Yes.Fit on Facebook"
                                            className="flex items-center justify-center text-gray-800 transition-all duration-200 bg-transparent border border-gray-300 rounded-full w-7 h-7 focus:bg-yf-green hover:text-white focus:text-white hover:bg-yf-green hover:border-yf-green focus:border-yf-green"
                                        >
                                            <svg
                                                className="w-4 h-4"
                                                xmlns="http://www.w3.org/2000/svg"
                                                viewBox="0 0 24 24"
                                                fill="currentColor"
                                            >
                                                <path d="M13.397 20.997v-8.196h2.765l.411-3.209h-3.176V7.548c0-.926.258-1.56 1.587-1.56h1.684V3.127A22.336 22.336 0 0 0 14.201 3c-2.444 0-4.122 1.492-4.122 4.231v2.355H7.332v3.209h2.753v8.202h3.312z"></path>
                                            </svg>
                                        </a>
                                    </li>

                                    <li>
                                        <a
                                            href="https://www.instagram.com/yesfitchallenges/?hl=en"
                                            title="Yes.Fit on Instagram"
                                            className="flex items-center justify-center text-gray-800 transition-all duration-200 bg-transparent border border-gray-300 rounded-full w-7 h-7 focus:bg-yf-green hover:text-white focus:text-white hover:bg-yf-green hover:border-yf-green focus:border-yf-green"
                                        >
                                            <svg
                                                className="w-4 h-4"
                                                xmlns="http://www.w3.org/2000/svg"
                                                viewBox="0 0 24 24"
                                                fill="currentColor"
                                            >
                                                <path d="M11.999 7.377a4.623 4.623 0 1 0 0 9.248 4.623 4.623 0 0 0 0-9.248zm0 7.627a3.004 3.004 0 1 1 0-6.008 3.004 3.004 0 0 1 0 6.008z"></path>
                                                <circle
                                                    cx="16.806"
                                                    cy="7.207"
                                                    r="1.078"
                                                ></circle>
                                                <path d="M20.533 6.111A4.605 4.605 0 0 0 17.9 3.479a6.606 6.606 0 0 0-2.186-.42c-.963-.042-1.268-.054-3.71-.054s-2.755 0-3.71.054a6.554 6.554 0 0 0-2.184.42 4.6 4.6 0 0 0-2.633 2.632 6.585 6.585 0 0 0-.419 2.186c-.043.962-.056 1.267-.056 3.71 0 2.442 0 2.753.056 3.71.015.748.156 1.486.419 2.187a4.61 4.61 0 0 0 2.634 2.632 6.584 6.584 0 0 0 2.185.45c.963.042 1.268.055 3.71.055s2.755 0 3.71-.055a6.615 6.615 0 0 0 2.186-.419 4.613 4.613 0 0 0 2.633-2.633c.263-.7.404-1.438.419-2.186.043-.962.056-1.267.056-3.71s0-2.753-.056-3.71a6.581 6.581 0 0 0-.421-2.217zm-1.218 9.532a5.043 5.043 0 0 1-.311 1.688 2.987 2.987 0 0 1-1.712 1.711 4.985 4.985 0 0 1-1.67.311c-.95.044-1.218.055-3.654.055-2.438 0-2.687 0-3.655-.055a4.96 4.96 0 0 1-1.669-.311 2.985 2.985 0 0 1-1.719-1.711 5.08 5.08 0 0 1-.311-1.669c-.043-.95-.053-1.218-.053-3.654 0-2.437 0-2.686.053-3.655a5.038 5.038 0 0 1 .311-1.687c.305-.789.93-1.41 1.719-1.712a5.01 5.01 0 0 1 1.669-.311c.951-.043 1.218-.055 3.655-.055s2.687 0 3.654.055a4.96 4.96 0 0 1 1.67.311 2.991 2.991 0 0 1 1.712 1.712 5.08 5.08 0 0 1 .311 1.669c.043.951.054 1.218.054 3.655 0 2.436 0 2.698-.043 3.654h-.011z"></path>
                                            </svg>
                                        </a>
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </section>
                )}
            </div>
        </div>
    )
}
